import { Button } from '@ipfabric/ui-components/Button/Button';
import { FormField } from '@ipfabric/ui-components/FormField/FormField';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { FloatingList } from '@ipfabric/ui-components/List/FloatingList/FloatingList';
import type { FloatingListItemContainerProps } from '@ipfabric/ui-components/List/FloatingList/FloatingList.types';
import { Select } from '@ipfabric/ui-components/Select/Select';
import { AUTO_ASSIGNMENT_RULE_FILTER_COLUMNS } from '@ipfabric/utils/schema/labels/autoAssignmentRuleFilters';
import {
  type Control,
  Controller,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import {
  CONDITION_OPERATOR_OPTIONS,
  type RuleFormValues,
  testColumnLabel,
} from './ruleFormSchema';
import { useRuleTargetType } from './useRuleTargetType';

type ConditionOperator = RuleFormValues['conditions'][number]['operator'];

type RuleConditionsEditorProps = {
  control: Control<RuleFormValues>;
  error?: string;
  onConditionsChange?: () => void;
};

export const RuleConditionsEditor = ({
  control,
  error,
  onConditionsChange,
}: RuleConditionsEditorProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'conditions',
  });

  const type = useRuleTargetType(control);
  const conditions = useWatch({ control, name: 'conditions' }) ?? [];
  const usedProperties = conditions.map((condition) => condition.property);

  const availablePropertyItems: FloatingListItemContainerProps[] =
    AUTO_ASSIGNMENT_RULE_FILTER_COLUMNS[type]
      .filter((column) => !usedProperties.includes(column))
      .map((column) => ({
        label: testColumnLabel(column),
        onClick: () => {
          append({
            property: column,
            operator: 'is',
            regex: '',
          });
          onConditionsChange?.();
        },
      }));

  return (
    <FormField
      description={
        <span className='inline-flex flex-wrap items-center gap-3xs'>
          Use
          <Icon glyph='fa-plus-circle' size='sm' />
          Add rule to specify rule targets.
        </span>
      }
      error={error}
      label='Rule setup'
      link={
        availablePropertyItems.length > 0 ? (
          <FloatingList items={availablePropertyItems} size='small'>
            {(setRef, setIsOpen, open) => (
              <Button
                dataTestId='rule-form-add-rule-link'
                icon='fa-plus-circle'
                iconPosition='start'
                onClick={() => setIsOpen(!open)}
                forwardRef={setRef}
                type='button'
                variant='plain'
              >
                Add rule
              </Button>
            )}
          </FloatingList>
        ) : undefined
      }
    >
      {fields.length > 0 ? (
        <div className='flex flex-col gap-2xs'>
          {fields.map((field, index) => (
            <ConditionRow
              key={field.id}
              control={control}
              index={index}
              onChange={onConditionsChange}
              onRemove={() => {
                remove(index);
                onConditionsChange?.();
              }}
            />
          ))}
        </div>
      ) : null}
    </FormField>
  );
};

type ConditionRowProps = {
  control: Control<RuleFormValues>;
  index: number;
  onChange?: () => void;
  onRemove: () => void;
};

const ConditionRow = ({
  control,
  index,
  onChange,
  onRemove,
}: ConditionRowProps) => {
  const property = useWatch({
    control,
    name: `conditions.${index}.property`,
  });

  const propertyLabel = property ? testColumnLabel(property) : property;

  return (
    <div className='flex items-center gap-xs'>
      <span className='text-sm font-medium min-w-[100px]'>{propertyLabel}</span>
      <Controller
        control={control}
        name={`conditions.${index}.operator`}
        render={({ field }) => (
          <Select.Mandatory<ConditionOperator>
            options={CONDITION_OPERATOR_OPTIONS.map((opt) => ({
              label: opt.label,
              value: opt.value,
            }))}
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
              onChange?.();
            }}
            size='default'
          />
        )}
      />
      <Controller
        control={control}
        name={`conditions.${index}.regex`}
        rules={{ required: 'Regex pattern is required.' }}
        render={({ field, fieldState }) => (
          <input
            className={`border rounded px-2 py-1.5 text-sm flex-1 ${fieldState.error ? 'border-red-500' : ''}`}
            type='text'
            placeholder='Regex pattern (required)'
            value={field.value}
            onChange={(e) => {
              field.onChange(e);
              onChange?.();
            }}
            onBlur={field.onBlur}
          />
        )}
      />
      <Button
        size='small'
        variant='plain'
        accent='negative'
        icon='fa-trash-can'
        onClick={onRemove}
        type='button'
      />
    </div>
  );
};
